// components/Configurator.js
import React, { useState, memo, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
// import { EffectComposer, Bloom } from '@react-three/postprocessing';
import { Grid, Center, GizmoHelper, GizmoViewport, AccumulativeShadows, RandomizedLight, OrbitControls, Environment } from '@react-three/drei';
import { 
  MenuIcon, 
} from 'lucide-react';
import Pergola3D from './Pergola3D';
import Sidebar from "./Sidebar"; 
import RequestForm from './RequestForm';
import EmailDebug from './EmailDebug';
import CanvasTest from './CanvasTest';
import { sendRequestEmail, isEmailJSConfigured, getSetupInstructions } from '../utils/emailService';
import LZString from 'lz-string';

// Shorten config keys to single characters (mapping)
const shortKeys = {
  width: "W",
  length: "L",
  height: "H",
  modules: "M",
  opening: "O",
  color: "C",
  glass: "G",
  glassSize: "GS",
  glassThickness: "GT",
  glassOpening: "GO",
  glassWalls: "GW",
  perimeterLed: "PL",
  roofLed: "RL",
  rainSensor: "RS",
  shades: "S",
  shadedWalls: "SW",
  zanzariere: "Z",
  motorized: "MT",
  wallMount: "WM",
  stripRotationHorizontal: "SRH"
};

// Reverse map for decoding
const longKeys = Object.fromEntries(Object.entries(shortKeys).map(([k, v]) => [v, k]));

// Encode function (shortens and compresses config)
const encodeConfig = (config) => {
  const shortConfig = Object.entries(config).reduce((acc, [key, value]) => {
      const shortKey = shortKeys[key] || key;
      if (typeof value === "boolean") {
          acc[shortKey] = value ? 1 : 0; // Convert booleans to 1 or 0
      } else if (typeof value === "object" && key === "glassWalls" || key === "shadedWalls") {
        //   acc[shortKey] = Object.keys(value).filter(wall => value[wall]).join(""); // Convert to string (e.g., "fronte,destra" → "FD")
        acc[shortKey] = Object.keys(value)
        .filter(wall => value[wall]) // Filter only selected walls
        .map(wall => wall.charAt(0).toUpperCase()) // Take only the first letter and uppercase it
        .join(""); // Join them into a string
    
        } else {
          acc[shortKey] = value;
      }
      return acc;
  }, {});

  return LZString.compressToEncodedURIComponent(JSON.stringify(shortConfig));
};

// Decode function
const decodeConfig = (encoded) => {
  try {
      const decodedConfig = JSON.parse(LZString.decompressFromEncodedURIComponent(encoded));
      if (!decodedConfig) return null;

      // Convert back to long keys
      return Object.entries(decodedConfig).reduce((acc, [key, value]) => {
          const longKey = longKeys[key] || key;
          if (value === 1 || value === 0) {
              acc[longKey] = value === 1; // Convert back to boolean
          } else if (longKey === "glassWalls" || longKey === "shadedWalls") {            
              acc[longKey] = { fronte: value.includes("F"), retro: value.includes("R"), sinistra: value.includes("S"), destra: value.includes("D") };
          } else {
              acc[longKey] = value;
          }
          return acc;
      }, {});
  } catch (error) {
      console.error("Error decoding config:", error);
      return null;
  }
};


function Configurator() {

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const defaultConfig = {
    width: 3, // cm
    length: 4, // cm
    height: 2.5, // cm
    modules: 1,
    opening: 'width', // 'width' or 'depth'
    color: 'white',
    glass: false,
    glassSize: 1.1, // cm (between 70-110 cm)
    glassThickness: 0.01, // 1 cm
    glassOpening: 'dx', // 'dx' (right) or 'sx' (left)
    glassWalls:{
        fronte: true,
        retro: true,
        sinistra: true,
        destra: true,
    },
    perimeterLed: false,
    roofLed: false,
    rainSensor: false,
    shades: false,
    shadedWalls:{
        fronte: false,
        retro: false,
        sinistra: false,
        destra: false,
    },
    zanzariere: false,
    motorized: false,
    wallMount: false,
    stripRotationHorizontal: false,
  };

    // **Load Values from URL (if present)**
    // const getConfigFromURL = () => {
    //     // const searchParams = new URLSearchParams(window.location.search);
        
    //     if (![...searchParams.keys()].length) {
    //         return { ...defaultConfig };
    //     }

    //     const newConfig = { ...defaultConfig };

    //     searchParams.forEach((value, key) => {
    //         if (value === 'true' || value === 'false') {
    //             newConfig[key] = value === 'true';
    //         } else if (!isNaN(parseFloat(value)) && isFinite(value)) {
    //             newConfig[key] = parseFloat(value);
    //         } else if (key === "glassWalls" || key === "shadedWalls") {
    //             // Convert comma-separated values into an object
    //             const wallsArray = value.split(",");
    //             newConfig[key] = {
    //                 fronte: wallsArray.includes("fronte"),
    //                 retro: wallsArray.includes("retro"),
    //                 sinistra: wallsArray.includes("sinistra"),
    //                 destra: wallsArray.includes("destra"),
    //             };
    //         } else {
    //             newConfig[key] = value;
    //         }
    //     });

    //     // Apply constraints
    //     newConfig.width = Math.min(4.5, Math.max(2.4, newConfig.width || defaultConfig.width));
    //     newConfig.length = Math.min(4.5, Math.max(2.4, newConfig.length || defaultConfig.length));
    //     newConfig.height = Math.min(3.5, Math.max(2.4, newConfig.height || defaultConfig.height));
    //     newConfig.glassSize = Math.min(1.1, Math.max(0.7, newConfig.glassSize || defaultConfig.glassSize));

    //     return newConfig;
    // };
    // Modify getConfigFromURL to use encoded params
    const getConfigFromURL = () => {
      const encodedConfig = searchParams.get("cfg");
      if (encodedConfig) {
          const decodedConfig = decodeConfig(encodedConfig);
          if (decodedConfig) return decodedConfig;
      }
      return { ...defaultConfig };
    };

    const [config, setConfig] = useState(getConfigFromURL);
    const [tempConfig, setTempConfig] = useState(() => ({ ...defaultConfig })); 
    const [debounceTimeout, setDebounceTimeout] = useState(null);

    const [area, setArea] = useState(12);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [darkMode, setDarkMode] = useState(true);
    const [infosVisible, setInfosVisible] = useState(true);
    const [showRequestForm, setShowRequestForm] = useState(false);
    
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const sceneRef = useRef();

    const toggleGlassWall = (wall) => {
        setConfig((prevConfig) => {
            const updatedWalls = { ...prevConfig.glassWalls, [wall]: !prevConfig.glassWalls[wall] };
            return {
                ...prevConfig,
                glassWalls: updatedWalls,
            };
        });
    };
    
    const toggleShadeWall = (wall) => {
        setConfig((prevConfig) => {
            const updatedWalls = { ...prevConfig.shadedWalls, [wall]: !prevConfig.shadedWalls[wall] };
            return {
                ...prevConfig,
                shadedWalls: updatedWalls,
            };
        });
    };

    const handleColorChange = (value) => {
        setConfig((prevConfig) => ({
            ...prevConfig,
            color: value,
        }));
    };


    useEffect(() => {
        const params = getConfigFromURL()
        // console.log('Params:', params);

        setConfig({ ...defaultConfig, ...params });
        
        const handleResize = () => {
            setWindowHeight(window.innerHeight);
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
        window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (config) {
            setTempConfig({ ...config });
        }
    }, [config]);

    // **Update URL when state changes**
//     useEffect(() => {
//     const updatedParams = new URLSearchParams();
//     Object.entries(config).forEach(([key, value]) => {
//         if (typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
//             updatedParams.set(key, value);
//         } else if (key === "glassWalls" || key === "shadedWalls") {
//             // Convert object to a comma-separated string
//             const wallsArray = Object.keys(value).filter(wall => value[wall]);
//             if (wallsArray.length > 0) {
//                 updatedParams.set(key, wallsArray.join(","));
//             }
//         }
//     });
//     navigate(`?${updatedParams.toString()}`, { replace: true });
// }, [config, navigate]);
  useEffect(() => {
    const encodedConfig = encodeConfig(config);
    navigate(`?cfg=${encodedConfig}`, { replace: true });
  }, [config, navigate]);


    // **Input Handlers**
    const handleConfigChange = (e) => {
        const { name, value } = e.target;
        // console.log('handleConfigChange:', name, value);
        
        if (!name) return; // Prevent undefined errors
    
        // Update tempConfig immediately
        setTempConfig((prev) => ({
            ...prev,
            [name]: value
        }));

        // console.log('tempConfig:', tempConfig);
            
        let updatedValue = parseFloat(value);

        if (name === 'width' || name === 'length' || name === 'height') {
            // updatedValue = value.replace(/^0+/, '');
            updatedValue = Math.max(0, Number(updatedValue));
    
            if (name === 'width') {
                // updatedValue = Math.max(2.4, updatedValue); // Min width
                const minModules = Math.max(1, Math.ceil(updatedValue / 4.5)); // At least 1 module per 4.5m                        
                setConfig((prev) => ({
                    ...prev,
                    width: updatedValue,
                    modules: Math.max(minModules, prev.modules), // Keep manual modules if higher
                }));
                setArea(Math.round(updatedValue * config.length)); // Use config instead of prev
            } else if (name === 'length') {
                updatedValue = Math.min(4.5, Math.max(2.4, updatedValue)); // Min 2.4m, Max 4.5m
                setConfig((prev) => ({ ...prev, length: updatedValue }));
                setArea(Math.round(config.width * updatedValue));
            } else if (name === 'height') {
                updatedValue = Math.min(3.5, Math.max(2.4, updatedValue));
                setConfig((prev) => ({ ...prev, height: updatedValue }));
            }
        } else if (name === 'modules') {
            const minModules = Math.max(1, Math.ceil(config.width / 4.5)); // Enforce minimum modules based on width                  
            const newModules = Math.max(minModules, Number(value)); // Allow user to increase modules but not decrease below min
            setConfig((prev) => ({ ...prev, modules: newModules }));
        } else if (name === 'color') {
            setConfig((prev) => ({ ...prev, [name]: value }));
        } else if (name === 'glass') {
            setConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'glassSize') {
            const sanitizedValue = Math.min(1.1, Math.max(0.7, value));
            setConfig((prev) => ({ ...prev, [name]: sanitizedValue }));
        } else if (name === 'glassOpening') {
            setConfig((prev) => ({ ...prev, [name]: value }));
        } else if (name === 'shades') {
            setConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'zanzariere') {
            setConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'motorized') {
            console.log('motorized:', value);
            
            setConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'perimeterLed') {
            setConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'roofLed') {
            setConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'rainSensor') {
            setConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        }
    };

    const handleTempConfigChange = (e) => {
        const { name, value } = e.target;
        
        if (!name) return; // Prevent undefined errors
            
        let updatedValue = parseFloat(value);

        if (name === 'width' || name === 'length' || name === 'height') {
            updatedValue = Math.max(0, Number(updatedValue));
    
            if (name === 'width') {
                const minModules = Math.max(1, Math.ceil(updatedValue / 4.5)); // At least 1 module per 4.5m                        
                setTempConfig((prev) => ({
                    ...prev,
                    width: updatedValue,
                    modules: Math.max(minModules, prev.modules), // Keep manual modules if higher
                }));
                setArea(Math.round(updatedValue * config.length)); // Use config instead of prev
            } else if (name === 'length') {
                setTempConfig((prev) => ({ ...prev, length: updatedValue }));
                setArea(Math.round(config.width * updatedValue));
            } else if (name === 'height') {
                setTempConfig((prev) => ({ ...prev, height: updatedValue }));
            }
        } else if (name === 'modules') {
            const minModules = Math.max(1, Math.ceil(config.width / 4.5)); // Enforce minimum modules based on width                  
            const newModules = Math.max(minModules, Number(value)); // Allow user to increase modules but not decrease below min
            setTempConfig((prev) => ({ ...prev, modules: newModules }));
        } else if (name === 'color') {
            setTempConfig((prev) => ({ ...prev, [name]: value }));
        } else if (name === 'glass') {
            setTempConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'glassSize') {
            const sanitizedValue = Math.min(1.1, Math.max(0.7, value));
            setTempConfig((prev) => ({ ...prev, [name]: sanitizedValue }));
        } else if (name === 'glassOpening') {
            setTempConfig((prev) => ({ ...prev, [name]: value }));
        } else if (name === 'shades') {
            setTempConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'zanzariere') {
            setTempConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'motorized') {            
            setTempConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'perimeterLed') {
            setTempConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'roofLed') {
            setTempConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        } else if (name === 'rainSensor') {
            setTempConfig((prev) => ({ ...prev, [name]: !prev[name] }));
        }
    };
    

    useEffect(() => {
        // console.log('Config changed:', config);
        setTempConfig(config);
    }, [config]);

    // **Handle Request Form Submission**
    const handleRequestSubmission = async (formData) => {
        try {
            // Check if EmailJS is configured
            if (!isEmailJSConfigured()) {
                console.warn('EmailJS not configured:', getSetupInstructions());
                
                // For development: show form data and config in console
                console.log('Form Data:', formData);
                console.log('Configuration:', config);
                console.log('Configuration URL:', window.location.href);
                
                alert('Richiesta ricevuta! (Demo mode - EmailJS non configurato)\n\nDati della richiesta sono stati registrati nella console per testing.\n\nPer abilitare l\'invio email, configura EmailJS nel file .env');
                return { success: true };
            }
            
            // Get canvas for PDF generation note
            const canvas = document.querySelector('canvas');
            
            // Send email using EmailJS
            const result = await sendRequestEmail(formData, config, window.location.href, canvas);
            
            if (result.success) {
                alert('✅ Richiesta inviata con successo!\n\nIl nostro team analizzerà la tua configurazione e ti contatterà entro 24 ore.\n\nGrazie per aver scelto TAAPP!');
                return { success: true };
            } else {
                throw new Error('Errore durante l\'invio della richiesta');
            }
            
        } catch (error) {
            console.error('Request submission error:', error);
            
            // Show specific error message to user
            let userMessage = '❌ Errore durante l\'invio della richiesta.\n\n';
            
            if (error.message.includes('EmailJS')) {
                userMessage += 'Problema di configurazione del servizio email.\n';
            } else if (error.message.includes('network') || error.message.includes('fetch')) {
                userMessage += 'Problema di connessione. Verifica la tua connessione internet.\n';
            } else {
                userMessage += 'Errore tecnico temporaneo.\n';
            }
            
            userMessage += '\nPuoi provare a:\n• Riprovare tra qualche minuto\n• Verificare la connessione internet\n• Contattarci direttamente a info@pergoleinnovative.it';
            
            alert(userMessage);
            throw error;
        }
    };
  
  return (
    <div 
      className="relative w-screen"
      style={{ height: `${windowHeight}px` }} // Dynamic height based on visible window
    >
        {/* Logo */}
        <div className="absolute top-4 right-4 z-30">
            <img src={`${process.env.PUBLIC_URL}/logo-tapp.png`} alt="Logo" className="w-20" />
        </div>

      {/* Sidebar Component */}
      <Sidebar
        config={config}
        setConfig={setConfig}
        tempConfig={tempConfig}
        handleTempConfigChange={handleTempConfigChange}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        infosVisible={infosVisible}
        setInfosVisible={setInfosVisible}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        handleConfigChange={handleConfigChange}
        handleColorChange={handleColorChange}
        area={area}
        sceneRef={sceneRef}
        toggleGlassWall={toggleGlassWall}
        toggleShadeWall={toggleShadeWall}
        showRequestForm={showRequestForm}
        setShowRequestForm={setShowRequestForm}
      />

      {/* Toggle Sidebar Button */}
      {!sidebarOpen&&<button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`absolute top-4 left-4 z-30 ${darkMode?'text-white':'text-black'} p-2 rounded`}
      >
        {/* {sidebarOpen ? 'Chiudi' : 'Menu'} */}
        <MenuIcon size={24} />
      </button>}

        {/* Canvas */}
        <Canvas 
            className={`${darkMode?'bg-neutral-700':'bg-neutral-200'}`} 
            shadows 
            camera={{ position: [15, 20, 20], fov: 25 }}
            gl={{ preserveDrawingBuffer: true }}
            onCreated={({ scene }) => (sceneRef.current = scene)}
        >
            <group position={[0, -0.5, 0]}>
              <Center top>
                  <Pergola3D
                      config={config}
                      tempConfig={tempConfig}
                      infosVisible={infosVisible}
                      darkMode={darkMode}
                  />
              </Center>
              {/* <Shadows /> */}

              {/* 🌟 Improved Lighting */}
              {/* <ambientLight intensity={0.5} />
              <pointLight position={[0, 5, 0]} intensity={1.5} /> */}

              {/* ✨ Bloom Effect for LED Glow */}
              {/* <EffectComposer>
                  <Bloom luminanceThreshold={0.1} luminanceSmoothing={0.9} intensity={0.5} />
              </EffectComposer> */}
              
              <Grid
                  position={[0, -0.01, 0]}
                  args={[10.5, 10.5]}
                  cellSize={0.6}
                  cellThickness={1}
                  cellColor={darkMode?"#000":"#ededed"}
                  sectionSize={3.3}
                  sectionThickness={1.5}
                  sectionColor={darkMode?"#808080":"#ccc"}
                  fadeDistance={35}
                  fadeStrength={1}
                  infiniteGrid={true}
              />
            </group>
            <OrbitControls 
                maxPolarAngle={Math.PI / 2}
                enableZoom={true}
                minDistance={2} // Minimum zoom distance
                maxDistance={40} // Maximum zoom distance
                makeDefault 
                enablePan={false}
            />
            <Environment preset="city" />
            {/* <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
              <GizmoViewport axisColors={["#9d4b4b", "#2f7f4f", "#3b5b9d"]} labelColor="white" />
            </GizmoHelper> */}
        </Canvas>

        {/* Request Form Modal */}
        <RequestForm
          isOpen={showRequestForm}
          onClose={() => setShowRequestForm(false)}
          config={config}
          onSubmit={handleRequestSubmission}
        />

        {/* Debug components for development */}
        <EmailDebug />
        <CanvasTest />
        
    </div>
  );
}

const Shadows = memo(() => (
  <AccumulativeShadows temporal frames={100} color="#9d4b4b" colorBlend={0.5} alphaTest={0.5} scale={50}>
    <RandomizedLight amount={8} radius={4} position={[5, 5, -10]} />
  </AccumulativeShadows>
));

export default Configurator;
